import { useCallback } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';

const requireToken = () => {
  const token = getAuthToken();
  if (!token || !isAuthenticated()) {
    notify.error(MESSAGES.authRequired);
    return null;
  }
  return token;
};

const parseResponse = async response => {
  const result = await response.json().catch(() => null);

  if (!response.ok || result?.success === false) {
    const message =
      result?.message || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return result;
};

const useAwardingApi = () => {
  const createAwarding = useCallback(async payload => {
    const token = requireToken();
    if (!token) return null;

    const response = await fetch(`${api.API_URL}/awarding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    return parseResponse(response);
  }, []);

  const getAwardingById = useCallback(async id => {
    const token = requireToken();
    if (!token) return null;

    const response = await fetch(`${api.API_URL}/awarding/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'same-origin',
    });

    return parseResponse(response);
  }, []);

  const getActivities = useCallback(async id => {
    const token = requireToken();
    if (!token) return null;

    const response = await fetch(`${api.API_URL}/awarding/${id}/activity`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'same-origin',
    });

    return parseResponse(response);
  }, []);

  const getRegisteredSuppliers = useCallback(async (search = '') => {
    const token = requireToken();
    if (!token) return null;

    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await fetch(
      `${api.API_URL}/awarding/registered-supplier${params}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    );

    return parseResponse(response);
  }, []);

  const uploadDocument = useCallback(
    async (
      id,
      file,
      {
        documentType = 1,
        companyMitraId = null,
        picName = '',
        tradingEmail = '',
      } = {}
    ) => {
      const token = requireToken();
      if (!token) return null;

      const formData = new FormData();
      formData.append('File', file);
      formData.append('DocumentType', String(documentType));
      if (companyMitraId) {
        formData.append('CompanyMitraId', companyMitraId);
      }
      if (picName) {
        formData.append('PicName', picName);
      }
      if (tradingEmail) {
        formData.append('TradingEmail', tradingEmail);
      }

      const response = await fetch(`${api.API_URL}/awarding/${id}/document`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'same-origin',
        body: formData,
      });

      return parseResponse(response);
    },
    []
  );

  const deleteDocument = useCallback(async documentId => {
    const token = requireToken();
    if (!token) return null;

    const response = await fetch(
      `${api.API_URL}/awarding/document/${documentId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    );

    return parseResponse(response);
  }, []);

  const getResponses = useCallback(async (id, viewerType = 1) => {
    const token = requireToken();
    if (!token) return null;

    const response = await fetch(
      `${api.API_URL}/awarding/${id}/response?viewerType=${viewerType}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    );

    return parseResponse(response);
  }, []);

  const createResponse = useCallback(async (id, payload) => {
    const token = requireToken();
    if (!token) return null;

    const response = await fetch(`${api.API_URL}/awarding/${id}/response`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    return parseResponse(response);
  }, []);

  const confirmDealRecap = useCallback(async id => {
    const token = requireToken();
    if (!token) return null;

    const response = await fetch(
      `${api.API_URL}/awarding/${id}/confirm-deal-recap`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    );

    return parseResponse(response);
  }, []);

  const sendDealRecap = useCallback(async (id, companyMitraId = null) => {
    const token = requireToken();
    if (!token) return null;

    const query = companyMitraId ? `?companyMitraId=${companyMitraId}` : '';
    const response = await fetch(
      `${api.API_URL}/awarding/${id}/send-deal-recap${query}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    );

    return parseResponse(response);
  }, []);

  return {
    createAwarding,
    getAwardingById,
    getActivities,
    getRegisteredSuppliers,
    getResponses,
    createResponse,
    confirmDealRecap,
    uploadDocument,
    deleteDocument,
    sendDealRecap,
  };
};

export default useAwardingApi;
